import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';

/**
 * States in which the approved total still lives in PRE_APPROVED_TOTAL rather than
 * FINAL_APPROVED_TOTAL. Mirrors the API's BEFORE_PRE_APPROVAL_STATE_STRINGS, which is
 * what `update-approved-total` uses to pick the field it writes and logs.
 */
export const BEFORE_PRE_APPROVAL_STATES = [
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
];

/** The approved-total field a write in this state lands in. */
export function approvedTotalFieldForState(state: string) {
  return BEFORE_PRE_APPROVAL_STATES.includes(state) ? 'PRE_APPROVED_TOTAL' : 'FINAL_APPROVED_TOTAL';
}

/**
 * Whether an approver's edit to course tuition/fees should carry through to the
 * application's approved total.
 *
 * The approved total is seeded from the requested total when the application is
 * submitted, but nothing re-seeds it when an approver corrects the expenses on the
 * approval page. The approve modal reads `approvedTotal` — and derives its
 * "X% reimbursement of" input from it — so without this the approver has to retype the
 * amount on every approval (SUP-7638).
 *
 * `requestedTotalBeforeEdit` is the requested total captured when the approver opened
 * the expense editor, and it is what makes this safe: the sync only runs while the
 * approved total is still *tracking* the request. Once somebody has deliberately set a
 * different amount — a first approver reducing it, or an edit through the
 * financial-details drawer — the two have diverged and the decision is left alone. It
 * has to be the pre-edit figure because by the time this is asked the edit has already
 * moved the live `requestedTotal`.
 *
 * Returns false wherever another rule already owns the total:
 * - Course Cash / direct pay: `setSubtotalsForDirectPay` already syncs the subtotals.
 * - Claims financials: the claims calculator owns the reimbursement total.
 * - Voucher programs: the amount is VOUCHER_AMOUNT, taken from the limit at approval.
 * - Anything past a pending decision: the total is no longer a proposal.
 * - Any ledger-contributing reimbursement assistance. This one is load-bearing:
 *   `TasApplication#update_approved_total` dispatches on the *existence* of such a row
 *   (`tas_assistances.contributes_to_reimbursement_limit_ledger_amount.any?`), not on
 *   amounts, and the assistance path it takes never writes FINAL_APPROVED_TOTAL — it
 *   raises a payment/repayment request for the delta instead. Gating on `paid` /
 *   `pending_payment` alone would let a $0 PAYABLE row or a FORGIVEN-only ledger read as
 *   "no money moving", so the sync would quietly file a payment request and then fail its
 *   own write-verification. `hasLedgerContributingReimbursement` mirrors the API scope
 *   exactly. The amounts are still checked, as a manner-agnostic backstop.
 */
export function canSyncApprovedTotalFromRequest(
  application: TasApplicationModel,
  requestedTotalBeforeEdit: number | null
) {
  const template = application.tasProgramInstance?.tasProgramTemplate;

  if (!template) {
    return false;
  }

  if (
    template.isDirectPayProgram &&
    application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
  ) {
    return false;
  }

  if (template.calculateTotalByClaimsFinancials || template.isVoucherProgram) {
    return false;
  }

  const { state } = application;
  const isPendingDecision =
    state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' ||
    state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';

  if (!isPendingDecision) {
    return false;
  }

  // Existence, not amount — see the note above. Requires `tas-assistances` to be loaded,
  // or this reads an empty array and fails open.
  if (application.hasLedgerContributingReimbursement) {
    return false;
  }

  const amounts = application.amounts;

  if ((amounts?.paid ?? 0) !== 0 || (amounts?.pending_payment ?? 0) !== 0) {
    return false;
  }

  // No captured edit session — nothing to compare against, so make no assumptions.
  if (requestedTotalBeforeEdit === null) {
    return false;
  }

  return application.approvedTotal === requestedTotalBeforeEdit;
}

export default null; // silence a false warning
